/**
 * ArtifactRenderer
 *
 * Renders artifact content based on contentType:
 * - html: sandboxed iframe
 * - markdown: rendered via react-markdown
 * - code: preformatted block with copy button
 * - text: plain preformatted text
 */

import { useState } from 'react';
import { Copy, Check, ImageOff } from 'lucide-react';
import { MarkdownContent } from '@/components/ui';
import type { Artifact } from '../../types/ui-events';

interface ArtifactRendererProps {
  artifact: Artifact;
}

export default function ArtifactRenderer({ artifact }: ArtifactRendererProps) {
  switch (artifact.contentType) {
    case 'image':
      return <ImageRenderer url={artifact.url} filename={artifact.filename} />;
    case 'html':
      return artifact.content
        ? <HtmlRenderer content={artifact.content} />
        : <NoContentPlaceholder />;
    case 'markdown':
      return artifact.content
        ? <MarkdownRenderer content={artifact.content} />
        : <NoContentPlaceholder />;
    case 'code':
      return artifact.content
        ? <CodeRenderer content={artifact.content} language={artifact.language} />
        : <NoContentPlaceholder />;
    case 'text':
      return artifact.content
        ? <TextRenderer content={artifact.content} />
        : <NoContentPlaceholder />;
    default:
      return artifact.content
        ? <TextRenderer content={artifact.content} />
        : <NoContentPlaceholder />;
  }
}

function HtmlRenderer({ content }: { content: string }) {
  return (
    <div className="flex-1 overflow-hidden">
      <iframe
        srcDoc={content}
        sandbox="allow-scripts"
        title="Artifact preview"
        className="h-full w-full border-0 bg-white"
      />
    </div>
  );
}

function MarkdownRenderer({ content }: { content: string }) {
  return (
    <div className="flex-1 overflow-y-auto p-4">
      <MarkdownContent size="sm">{content}</MarkdownContent>
    </div>
  );
}

function CodeRenderer({ content, language }: { content: string; language?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        {language && (
          <span className="text-xs font-medium text-muted-foreground">
            {language}
          </span>
        )}
        <button
          type="button"
          onClick={handleCopy}
          className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <pre className="text-xs font-mono text-foreground whitespace-pre-wrap">
          <code>{content}</code>
        </pre>
      </div>
    </div>
  );
}

function TextRenderer({ content }: { content: string }) {
  return (
    <div className="flex-1 overflow-y-auto p-4">
      <pre className="text-sm text-foreground whitespace-pre-wrap font-sans">
        {content}
      </pre>
    </div>
  );
}

function ImageRenderer({ url, filename }: { url?: string; filename?: string }) {
  const [error, setError] = useState(false);

  if (!url || error) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <ImageOff className="h-8 w-8" />
          <span className="text-sm">{error ? 'Failed to load image' : 'No image URL available'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-center overflow-auto p-4">
      <img
        src={url}
        alt={filename ?? 'Artifact image'}
        className="max-h-full max-w-full object-contain"
        onError={() => setError(true)}
      />
    </div>
  );
}

function NoContentPlaceholder() {
  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <span className="text-sm text-muted-foreground">No content available</span>
    </div>
  );
}
